/**
 * Phase 4g.C (Feb 2026) — Werkstatt-Auftragskarte (extrahiert aus
 * `WorkshopOrdersPage.jsx`, ~170 Zeilen).
 *
 * Read-only-Anzeige eines einzelnen Auftrags + Action-Buttons-Footer.
 * Alle Aktionen werden über Callback-Props nach oben durchgereicht
 * (`onEdit`, `onTransition`, etc.) — kein Daten-Mutations-State hier.
 *
 * Public API (Props):
 *   order          — Auftragsobjekt (Pflicht)
 *   customer       — Aufgelöster Kunden-Datensatz (optional, kann null sein)
 *   highlighted    — Boolean: gelb umrahmen (z.B. nach Inbox-Convert)
 *   onEdit/onDelete/onTransition/onConvert/onQuotePdf/onQuoteMail/
 *   onQrSticker/onQrPreview/onShare/onTimer/onSendReminder — Callbacks.
 *
 * Snapshot-Tests in `OrderCard.test.jsx` halten das visuelle Verhalten
 * stabil für die wichtigsten Status-Zustände.
 */
import { useState } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { confirmDialog } from "../../lib/confirmDialog";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from "../ui/dropdown-menu";
import {
    Edit2, Trash2, ArrowRight, FileText, Check, Package, Download, Mail, Bell,
    QrCode, Share2, Timer, Wrench, X, Printer, Clock, ChevronDown, UserCheck, CheckCircle2, Camera,
    CalendarClock, AlertTriangle, Smartphone,
} from "lucide-react";

import { STATUS_META, STATUS_NEXT_LABEL, fmtEur, orderCategories, plateLabel } from "./workshopConstants";

function fmtDateShort(s) {
    const d = (s || "").slice(0, 10);
    return d.length >= 10 ? `${d.slice(8, 10)}.${d.slice(5, 7)}.${d.slice(0, 4)}` : d;
}

// Jul-2026 (User-Wunsch) — erfasste Arbeitszeit lesbar formatieren (Std/Min).
function fmtMin(min) {
    const m = Number(min) || 0;
    if (m <= 0) return "0 Min";
    const h = Math.floor(m / 60);
    return h > 0 ? `${h} Std ${m % 60} Min` : `${m} Min`;
}

export default function OrderCard({
    order, customer, highlighted,
    onEdit, onDelete, onTransition, onConvert, onQuotePdf, onQuoteMail,
    onQrSticker, onQrPreview, onShare, onTimer, onSendReminder, onPrintOrder,
    onPlanCalendar, onNotifyDelay,
}) {
    const meta = STATUS_META[order.status] || STATUS_META.draft;
    // Jul-2026 (User-Wunsch) — Löschen nur für Filialleiter/Inhaber.
    const { user } = useAuth();
    const canDelete = user?.role === "b2b" || user?.role === "admin" || user?.employee_function === "filialleiter";
    // Jul-2026 (User-Wunsch) — Storno-Grund-Dialog
    const [cancelAsk, setCancelAsk] = useState(false);
    const [cancelReason, setCancelReason] = useState("");
    // Aug-2026 (User-Wunsch) — KVA-Freigabe per Handy-Link (kopieren / WhatsApp)
    const shareQuoteLink = async (mode) => {
        try {
            const r = await api.get(`/work-orders/${order.id}/quote-link`);
            const url = r.data.url || `${window.location.origin}${r.data.path}`;
            if (mode === "whatsapp") {
                const txt = `Guten Tag, hier können Sie Ihren Kostenvoranschlag ${order.order_number || ""} ansehen und direkt freigeben: ${url}`;
                window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, "_blank", "noopener");
            } else {
                await navigator.clipboard.writeText(url);
                toast.success("Freigabe-Link kopiert — Kunde kann den KV am Handy öffnen und direkt freigeben");
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Freigabe-Link konnte nicht erstellt werden");
        }
    };
    const cats = orderCategories(order);
    const veh = order.vehicle || {};
    const vehLabel = [veh.brand, veh.model].filter(Boolean).join(" ") || "—";
    // Jul-2026 (User-Wunsch): interne Aufträge (auf Fahrzeug gebucht) klar kennzeichnen.
    const custLabel = customer?.type === "business" && customer?.company_name
        ? customer.company_name
        : `${customer?.first_name || ""} ${customer?.last_name || ""}`.trim()
            || (order?.internal_vehicle_listing_id
                ? (order.internal_buyer_name
                    ? `Intern · Käufer: ${order.internal_buyer_name}`
                    : "Interner Auftrag (auf Fahrzeug gebucht)")
                : "—");
    const isLocked = order.status === "invoiced" || order.status === "cancelled";
    const partsCount = (order.positions || []).filter((p) => p.type === "part").length;
    const labourCount = (order.positions || []).filter((p) => p.type === "labour").length;
    // Jun-2026 (User-Wunsch) — Wunschtermin (Fertigstellung/Abholung) überschritten?
    const wunschISO = (order.estimated_completion || "").slice(0, 10);
    const overdue = !!wunschISO
        && wunschISO < new Date().toISOString().slice(0, 10)
        && !["completed", "invoiced", "cancelled"].includes(order.status);
    // Aug-2026 (User-Wunsch) — Termin-Ampel: rot überschritten · gelb ≤ 3 Tage · grün im Plan
    const terminDaysLeft = wunschISO
        ? Math.round((new Date(`${wunschISO}T00:00:00`) - new Date(`${new Date().toISOString().slice(0, 10)}T00:00:00`)) / 86400000)
        : null;
    const terminLabel = order.order_type === "delivery_internal" ? "Auslieferung" : "Wunschtermin";
    // Jun-2026 (User-Wunsch) — KV-PDF/KV-Mail bzw. Auftrag drucken/Auftragsmappe
    // jeweils in EIN Dropdown zusammengefasst (weniger Knöpfe in der Liste).
    const hasPositions = (order.positions?.length || 0) > 0;
    const canQuotePdf = ["draft", "quote", "confirmed", "in_progress", "completed"].includes(order.status) && hasPositions;
    const canQuoteMail = ["draft", "quote", "confirmed", "in_progress"].includes(order.status) && hasPositions;
    const canPrint = !["cancelled"].includes(order.status);

    return (
        <div
            className={`relative bg-white border rounded-sm p-4 pl-5 hover:border-[#111827] transition-colors ${order.source === "self_service" ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/50 bg-[#FFFBEB]" : highlighted ? "border-[#FFCC00] ring-2 ring-[#FFCC00]/30" : "border-gray-200"}`}
            data-testid={`wo-row-${order.id}`}
        >
            {/* Abrechnungs-Farbbalken links — bei Misch-Aufträgen segmentiert */}
            <div className="absolute left-0 top-0 bottom-0 w-2 flex flex-col overflow-hidden rounded-l-sm" data-testid={`wo-catbar-${order.id}`} aria-hidden="true">
                {cats.map((c) => (
                    <div key={c.key} className="flex-1" style={{ backgroundColor: c.hex }} title={c.label} />
                ))}
            </div>
            <div className="flex flex-wrap gap-3 items-start justify-between">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono-num font-black text-base">{order.order_number}</span>
                        <Badge className={`${meta.color} font-bold uppercase tracking-wider text-[10px] rounded-sm`} data-testid={`wo-status-${order.id}`}>
                            {meta.label}
                        </Badge>
                        {/* Jul-2026 (User-Wunsch) — auf einen Blick: Termin geplant? Zeit erfasst? */}
                        {order.appointment_at && !["invoiced", "cancelled"].includes(order.status) && (
                            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-300 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-scheduled-badge-${order.id}`} title="Auftrag ist im Kalender eingeplant">
                                <CalendarClock className="w-2.5 h-2.5 mr-0.5 inline" />
                                Eingeplant · {new Date(order.appointment_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}
                            </Badge>
                        )}
                        {order.time_running ? (
                            <Badge className="bg-teal-600 text-white border border-teal-700 font-bold text-[9px] rounded-sm uppercase tracking-wider animate-pulse" data-testid={`wo-time-badge-${order.id}`} title={`Stempeluhr läuft — bisher ${fmtMin(order.time_tracked_min)} erfasst`}>
                                <Timer className="w-2.5 h-2.5 mr-0.5 inline" />
                                Zeit läuft{Number(order.time_tracked_min) > 0 ? ` · ${fmtMin(order.time_tracked_min)}` : ""}
                            </Badge>
                        ) : Number(order.time_tracked_min) > 0 && (
                            <Badge className="bg-teal-50 text-teal-900 border border-teal-300 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-time-badge-${order.id}`} title="Bereits erfasste Arbeitszeit (Stempeluhr)">
                                <Timer className="w-2.5 h-2.5 mr-0.5 inline" />
                                Zeit erfasst · {fmtMin(order.time_tracked_min)}
                            </Badge>
                        )}
                        {order.source === "self_service" && (
                            <Badge className="bg-amber-500 text-white border border-amber-600 font-bold text-[9px] rounded-sm uppercase tracking-wider animate-pulse" data-testid={`wo-self-service-${order.id}`} title="24/7-Selbstannahme — bitte prüfen & freigeben (Pflichtdaten erforderlich)">
                                <Camera className="w-2.5 h-2.5 mr-0.5 inline" />
                                Selbstannahme · prüfen
                            </Badge>
                        )}
                        {order.created_outside_hours && (
                            <Badge className="bg-indigo-50 text-indigo-800 border border-indigo-200 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-outside-hours-${order.id}`} title="Außerhalb der Öffnungszeiten erstellt">
                                <Clock className="w-2.5 h-2.5 mr-0.5 inline" />
                                24h
                            </Badge>
                        )}
                        {cats.map((c) => (
                            <span
                                key={c.key}
                                className="inline-flex items-center gap-1 text-[9px] font-bold uppercase tracking-wider text-gray-700 bg-gray-50 border border-gray-200 rounded-sm px-1.5 py-0.5"
                                data-testid={`wo-cat-${order.id}-${c.key}`}
                            >
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.hex }} />
                                {c.label}
                            </span>
                        ))}
                        {order.stock_booked && (
                            <Badge className="bg-green-50 text-green-700 border border-green-200 font-bold text-[9px] rounded-sm uppercase tracking-wider">
                                <Package className="w-2.5 h-2.5 mr-0.5 inline" />
                                Lager gebucht
                            </Badge>
                        )}
                        {order.status === "cancelled" && (order.stock_returned_count || 0) > 0 && (
                            <Badge className="bg-orange-50 text-orange-800 border border-orange-200 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-stock-returned-${order.id}`} title="Beim Storno wurden die abgebuchten Teile automatisch zurück ins Lager gebucht">
                                <Package className="w-2.5 h-2.5 mr-0.5 inline" />
                                {order.stock_returned_count} Teile-Pos. zurückgebucht
                            </Badge>
                        )}
                        {order.invoice_id && (
                            <Badge className="bg-emerald-50 text-emerald-800 border border-emerald-200 font-bold text-[9px] rounded-sm uppercase tracking-wider">
                                <FileText className="w-2.5 h-2.5 mr-0.5 inline" />
                                Rechnung erstellt
                            </Badge>
                        )}
                        {overdue && (
                            <Badge className="bg-red-100 text-red-800 border border-red-300 font-bold text-[9px] rounded-sm uppercase tracking-wider animate-pulse" data-testid={`wo-overdue-${order.id}`} title={`${terminLabel} war ${fmtDateShort(wunschISO)} — überschritten.`}>
                                <span className="inline-block w-2 h-2 rounded-full bg-red-500 mr-1" />
                                {terminLabel} überschritten · {fmtDateShort(wunschISO)}
                            </Badge>
                        )}
                        {!overdue && wunschISO && !["completed", "invoiced", "cancelled"].includes(order.status) && (() => {
                            const soon = terminDaysLeft !== null && terminDaysLeft <= 3;
                            return (
                                <Badge
                                    className={`${soon ? "bg-amber-50 text-amber-900 border-amber-300" : "bg-emerald-50 text-emerald-800 border-emerald-200"} border font-bold text-[9px] rounded-sm uppercase tracking-wider`}
                                    data-testid={`wo-termin-ampel-${order.id}`}
                                    title={`${terminLabel} ${fmtDateShort(wunschISO)} — ${soon ? "rückt näher" : "im Plan"}`}
                                >
                                    <span className={`inline-block w-2 h-2 rounded-full mr-1 ${soon ? "bg-amber-500" : "bg-emerald-500"}`} />
                                    {terminLabel} {fmtDateShort(wunschISO)} · {terminDaysLeft === 0 ? "heute fällig" : terminDaysLeft === 1 ? "morgen" : soon ? `in ${terminDaysLeft} Tagen` : "im Plan"}
                                </Badge>
                            );
                        })()}
                        {order.followup_category && (
                            <Badge className="bg-indigo-50 text-indigo-800 border border-indigo-200 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-followup-${order.id}`}>
                                Folge-Auftrag · {order.cost_center || order.followup_category}
                                {order.follow_up_of_number ? ` ← ${order.follow_up_of_number}` : ""}
                            </Badge>
                        )}
                        {!order.followup_category && order.follow_up_of_number && (
                            <Badge className="bg-indigo-50 text-indigo-800 border border-indigo-200 font-bold text-[9px] rounded-sm uppercase tracking-wider" data-testid={`wo-followup-${order.id}`}>
                                Folge zu {order.follow_up_of_number}
                            </Badge>
                        )}
                        {order.order_signed_confirmed_at && (
                            <Badge className="bg-green-100 text-green-800 border border-green-300 font-bold text-[9px] rounded-sm uppercase tracking-wider"
                                data-testid={`wo-signed-verified-${order.id}`}
                                title={`Rechtssicher signiertes Exemplar liegt vor.${(order.signed_order_archive?.length ? ` SHA-256: ${order.signed_order_archive[order.signed_order_archive.length - 1].sha256?.slice(0, 16)}…` : "")}`}>
                                <CheckCircle2 className="w-2.5 h-2.5 mr-0.5 inline" />
                                Verbindlich angenommen · {new Date(order.order_signed_confirmed_at).toLocaleDateString("de-DE")}
                            </Badge>
                        )}
                    </div>
                    <div className="mt-1.5 grid grid-cols-2 md:grid-cols-7 gap-x-4 gap-y-0.5 text-sm">
                        <div className="text-gray-700"><span className="text-gray-400 text-[10px] uppercase tracking-wider block">Kunde</span>{custLabel}</div>
                        <div className="text-gray-700">
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Fahrzeug</span>
                            {vehLabel}
                            {plateLabel(veh) && <span className="ml-1 font-mono-num text-[11px] text-gray-500">· {plateLabel(veh)}</span>}
                        </div>
                        <div className="text-gray-700">
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Positionen</span>
                            <span className="font-bold">{order.positions?.length || 0}</span>
                            <span className="text-[11px] text-gray-500"> ({labourCount} Lohn · {partsCount} Teile)</span>
                        </div>
                        <div className="text-gray-700" data-testid={`wo-dropoff-${order.id}`}>
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Abgabe</span>
                            {order.created_at
                                ? <span className="font-mono-num text-[13px]">{new Date(order.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}</span>
                                : <span className="text-gray-400">—</span>}
                        </div>
                        <div className="text-gray-700" data-testid={`wo-appointment-${order.id}`}>
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Termin</span>
                            {order.appointment_at
                                ? <span className="font-mono-num text-[13px]">{new Date(order.appointment_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" })}</span>
                                : <span className="text-gray-400">—</span>}
                        </div>
                        <div className="text-gray-700" data-testid={`wo-completion-${order.id}`}>
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Fertigstellung</span>
                            {wunschISO
                                ? <span className={`font-mono-num text-[13px] ${overdue ? "text-red-700 font-bold" : ""}`}>{fmtDateShort(wunschISO)}</span>
                                : <span className="text-gray-400">—</span>}
                        </div>
                        <div className="text-gray-700" data-testid={`wo-time-${order.id}`}>
                            <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Arbeitszeit</span>
                            {order.time_running
                                ? <span className="font-mono-num text-[13px] text-teal-700 font-bold">läuft ⏱ {Number(order.time_tracked_min) > 0 ? `· ${fmtMin(order.time_tracked_min)}` : ""}</span>
                                : Number(order.time_tracked_min) > 0
                                    ? <span className="font-mono-num text-[13px] text-teal-800 font-bold">{fmtMin(order.time_tracked_min)}</span>
                                    : <span className="text-gray-400">—</span>}
                        </div>
                    </div>
                    {/* Jul-2026 (User-Wunsch) — Storno-Grund auf der Karte anzeigen */}
                    {order.status === "cancelled" && order.cancel_reason && (
                        <div className="mt-1.5 text-[12px] text-red-800 bg-red-50 border border-red-200 rounded-sm px-2 py-1 inline-block" data-testid={`wo-cancel-reason-display-${order.id}`}>
                            <span className="font-bold uppercase text-[10px] tracking-wider">Storno-Grund:</span> „{order.cancel_reason}&quot;
                        </div>
                    )}
                    {/* Jul-2026 (User-Wunsch) — Kundenauftrag IMMER sichtbar (auch wenn leer) */}
                    <div className="mt-2" data-testid={`wo-complaint-${order.id}`}>
                        <span className="text-gray-400 text-[10px] uppercase tracking-wider block">Kundenauftrag</span>
                        {order.customer_complaint
                            ? <span className="text-[12px] text-gray-600 italic border-l-2 border-gray-300 pl-2">„{order.customer_complaint}&quot;</span>
                            : <span className="text-[12px] text-gray-400">— nicht erfasst —</span>}
                    </div>
                    {order.employee && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-900 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-sm" data-testid={`wo-employee-badge-${order.id}`}>
                            <Wrench className="w-3 h-3" />
                            <span className="font-bold">{order.employee}</span>
                        </div>
                    )}
                    {order.accepted_by_label && (
                        <div className="mt-2 ml-0 sm:ml-2 inline-flex items-center gap-1 text-[11px] text-gray-600 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-sm" data-testid={`wo-accepted-by-${order.id}`} title="Angenommen von">
                            <UserCheck className="w-3 h-3" />
                            <span>Angenommen von: <span className="font-bold">{order.accepted_by_label}</span></span>
                        </div>
                    )}
                </div>
                <div className="text-right shrink-0">
                    {/* Aug-2026 (User-Wunsch): Im AUFTRAG zählt der NETTO-Betrag —
                        die MwSt kommt erst in der Rechnung dazu. */}
                    <div className="text-[10px] uppercase tracking-wider text-gray-500">Netto</div>
                    <div className="font-mono-num font-black text-xl" data-testid={`wo-total-net-${order.id}`}>{fmtEur(order.totals?.total_net)}</div>
                    <div className="text-[10px] text-gray-500">brutto {fmtEur(order.totals?.total_gross)}</div>
                </div>
            </div>

            <div className="mt-3 pt-3 border-t border-gray-100 flex gap-1.5 flex-wrap justify-end">
                {!["cancelled", "invoiced"].includes(order.status) && (
                    order.status === "confirmed" && !order.appointment_at ? (
                        <Button
                            size="sm"
                            onClick={() => onPlanCalendar?.(order)}
                            className="h-7 text-[11px] rounded-sm font-bold bg-[#111827] hover:bg-black text-white"
                            title="Termin & Hebebühne wählen — beim Speichern wird der Auftrag direkt gestartet (In Arbeit)"
                            data-testid={`wo-calendar-${order.id}`}
                        >
                            <CalendarClock className="w-3 h-3 mr-1" />
                            Einplanen & Start
                        </Button>
                    ) : (
                        <Button
                            size="sm" variant="outline"
                            onClick={() => onPlanCalendar?.(order)}
                            className="h-7 text-[11px] rounded-sm border-[#111827]/30 text-[#111827] hover:bg-gray-50"
                            title={order.appointment_at ? "Termin im Kalender anzeigen" : "Auftrag im Kalender einplanen (Termin & Hebebühne wählen)"}
                            data-testid={`wo-calendar-${order.id}`}
                        >
                            <CalendarClock className="w-3 h-3 mr-1" />
                            {order.appointment_at ? "Im Kalender" : "Einplanen"}
                        </Button>
                    )
                )}
                {overdue && (
                    <Button
                        size="sm" variant="outline"
                        onClick={() => onNotifyDelay?.(order)}
                        className="h-7 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50 font-bold"
                        title={order.delay_notified_at ? `Kunde informiert am ${new Date(order.delay_notified_at).toLocaleString("de-DE")}` : "Kunde über die Verzögerung informieren (E-Mail + WhatsApp/SMS)"}
                        data-testid={`wo-notify-delay-${order.id}`}
                    >
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {order.delay_notified_at ? "Verzögerung gemeldet ✓" : "Verzögerung melden"}
                    </Button>
                )}
                {!isLocked && (
                    <Button size="sm" variant="outline" onClick={onEdit} className="h-7 text-[11px] rounded-sm" data-testid={`wo-edit-${order.id}`}>
                        <Edit2 className="w-3 h-3 mr-1" /> Bearbeiten
                    </Button>
                )}
                {canQuotePdf && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-purple-300 text-purple-800 hover:bg-purple-50" data-testid={`wo-kv-menu-${order.id}`}>
                                <FileText className="w-3 h-3 mr-1" /> Kostenvoranschlag
                                {order.quote_sent_at && <span className="ml-1 text-[9px] text-amber-700">✓</span>}
                                <ChevronDown className="w-3 h-3 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={onQuotePdf} data-testid={`wo-quote-pdf-${order.id}`}>
                                <Download className="w-3.5 h-3.5 mr-2" /> KV als PDF
                            </DropdownMenuItem>
                            {canQuoteMail && (
                                <DropdownMenuItem onClick={onQuoteMail} data-testid={`wo-quote-mail-${order.id}`}>
                                    <Mail className="w-3.5 h-3.5 mr-2" /> KV per E-Mail{order.quote_sent_at ? " ✓" : ""}
                                </DropdownMenuItem>
                            )}
                            {canQuoteMail && (
                                <DropdownMenuItem onClick={() => shareQuoteLink("copy")} data-testid={`wo-quote-link-${order.id}`}>
                                    <Smartphone className="w-3.5 h-3.5 mr-2" /> Freigabe-Link kopieren (Handy)
                                </DropdownMenuItem>
                            )}
                            {canQuoteMail && (
                                <DropdownMenuItem onClick={() => shareQuoteLink("whatsapp")} data-testid={`wo-quote-whatsapp-${order.id}`}>
                                    <Share2 className="w-3.5 h-3.5 mr-2" /> Freigabe-Link per WhatsApp
                                </DropdownMenuItem>
                            )}
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {order.quote_rejected_at && (
                    <span className="inline-flex items-center h-7 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-2" data-testid={`wo-kv-rejected-${order.id}`}>
                        KV abgelehnt · Gebühr {fmtEur(order.quote_rejection_fee)}
                    </span>
                )}
                {order.quote_accepted_at && !order.quote_rejected_at && (
                    <span className="inline-flex items-center h-7 text-[10px] font-bold text-green-700 bg-green-50 border border-green-200 rounded-sm px-2" data-testid={`wo-kv-accepted-${order.id}`}>
                        KV online angenommen ✓
                    </span>
                )}
                {!["cancelled"].includes(order.status) && (
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button size="sm" variant="outline" className="h-7 text-[11px] rounded-sm border-gray-400 text-gray-800 hover:bg-gray-50" data-testid={`wo-print-menu-${order.id}`}>
                                <Printer className="w-3 h-3 mr-1" /> Drucken
                                <ChevronDown className="w-3 h-3 ml-1" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                            {canPrint && (
                                <DropdownMenuItem onClick={onPrintOrder} data-testid={`wo-print-${order.id}`}>
                                    <Printer className="w-3.5 h-3.5 mr-2" /> Auftrag drucken (PDF)
                                </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={onQrPreview} data-testid={`wo-qr-${order.id}`}>
                                <QrCode className="w-3.5 h-3.5 mr-2" /> QR-Nachdruck
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                )}
                {["confirmed", "in_progress"].includes(order.status) && (
                    <Button
                        size="sm" variant="outline"
                        onClick={onTimer}
                        className="h-7 text-[11px] rounded-sm border-teal-400 text-teal-900 hover:bg-teal-50"
                        title="Stempeluhr · Arbeitszeit erfassen"
                        data-testid={`wo-timer-${order.id}`}
                    >
                        <Timer className="w-3 h-3 mr-1" /> Zeit
                    </Button>
                )}
                {!["invoiced", "cancelled"].includes(order.status) && order.share_status !== "accepted" && (
                    <Button
                        size="sm" variant="outline"
                        onClick={onShare}
                        className="h-7 text-[11px] rounded-sm border-indigo-400 text-indigo-900 hover:bg-indigo-50"
                        title="Auftrag an anderen Händler teilen (Kapazitäts-Sharing)"
                        data-testid={`wo-share-${order.id}`}
                    >
                        <Share2 className="w-3 h-3 mr-1" />
                        {order.share_status === "pending" ? "Geteilt ⏳" : order.share_status === "declined" ? "Erneut teilen" : "Teilen"}
                    </Button>
                )}
                {order.appointment_at && ["confirmed", "in_progress"].includes(order.status) && (
                    <Button
                        size="sm" variant="outline"
                        onClick={() => onSendReminder?.(order)}
                        className="h-7 text-[11px] rounded-sm border-blue-300 text-blue-800 hover:bg-blue-50"
                        title={order.reminder_disabled
                            ? "Erinnerungen für diesen Auftrag deaktiviert"
                            : order.reminder_sent_at
                              ? `Erinnerung versendet am ${new Date(order.reminder_sent_at).toLocaleString('de-DE')}`
                              : "Termin-Erinnerung jetzt versenden"}
                        data-testid={`wo-reminder-${order.id}`}
                    >
                        <Bell className="w-3 h-3 mr-1" />
                        {order.reminder_disabled ? "Erinnerung Aus" : order.reminder_sent_at ? "Erinnert ✓" : "Erinnern"}
                    </Button>
                )}
                {!["draft", "quote", "invoiced", "cancelled"].includes(order.status) && hasPositions && !order.invoice_id && (
                    <Button
                        size="sm"
                        onClick={onConvert}
                        className="h-7 text-[11px] rounded-sm font-bold bg-emerald-600 hover:bg-emerald-700 text-white"
                        title="Rechnung erstellen — öffnet zuerst eine Vorschau aller Positionen zur Kontrolle"
                        data-testid={`wo-bill-${order.id}`}
                    >
                        <FileText className="w-3 h-3 mr-1" /> Rechnung erstellen
                    </Button>
                )}
                {/* Jul-2026 (User-Wunsch) — Löschen NUR durch Filialleiter/Inhaber;
                    alle anderen Mitarbeiter dürfen nur stornieren. */}
                {order.status !== "invoiced" && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={canDelete ? onDelete : () => toast.info("Nur der Filialleiter darf Aufträge löschen — bitte stattdessen „Stornieren“ nutzen.")}
                        className={`h-7 text-[11px] rounded-sm ${canDelete ? "text-red-700 hover:bg-red-50" : "text-gray-400 cursor-not-allowed hover:bg-gray-50"}`}
                        title={canDelete ? "Auftrag endgültig löschen (nur Filialleiter)" : "Löschen nur durch den Filialleiter möglich — bitte stattdessen stornieren."}
                        data-testid={`wo-del-${order.id}`}
                        aria-label="Auftrag löschen"
                    >
                        <Trash2 className="w-3 h-3" />
                    </Button>
                )}
                {(meta.next || []).filter((s) => s !== "invoiced" && !(s === "in_progress" && order.status === "confirmed" && !order.appointment_at)).map((s) => (
                    <Button
                        key={s}
                        size="sm"
                        onClick={() => s === "invoiced" ? onConvert() : onTransition(s)}
                        className={`h-7 text-[11px] rounded-sm font-bold ${s === "invoiced" ? "bg-emerald-600 hover:bg-emerald-700 text-white" : "bg-[#111827] hover:bg-black text-white"}`}
                        data-testid={`wo-next-${order.id}-${s}`}
                    >
                        {s === "completed" ? <Check className="w-3 h-3 mr-1" /> : s === "invoiced" ? <FileText className="w-3 h-3 mr-1" /> : <ArrowRight className="w-3 h-3 mr-1" />}
                        {STATUS_NEXT_LABEL[s] || s}
                    </Button>
                ))}
                {!isLocked && order.status !== "draft" && (
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setCancelReason(""); setCancelAsk(true); }}
                        className="h-7 text-[11px] rounded-sm border-red-300 text-red-700 hover:bg-red-50"
                        title="Auftrag ungültig machen (keine Abrechnung). Abgebuchte Lagerteile werden automatisch zurückgebucht."
                        data-testid={`wo-cancel-${order.id}`}
                    >
                        <X className="w-3 h-3 mr-1" /> Stornieren
                    </Button>
                )}
            </div>

            {/* Jul-2026 (User-Wunsch) — Storno mit Grund-Abfrage */}
            {cancelAsk && (
                <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid={`wo-cancel-dialog-${order.id}`}>
                    <div className="bg-white max-w-md w-full rounded-sm shadow-xl p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
                        <div className="font-display font-black text-lg">Auftrag {order.order_number} stornieren</div>
                        <p className="text-[12px] text-gray-600">
                            Es wird KEINE Rechnung erstellt. Bereits abgebuchte Lagerteile werden automatisch
                            zurück ins Lager gebucht. Der Auftrag bleibt zum Nachschlagen erhalten.
                        </p>
                        <p className="text-[12px] font-bold text-orange-700 bg-orange-50 border border-orange-200 rounded-sm p-2" data-testid={`wo-cancel-leader-info-${order.id}`}>
                            Hinweis: Der Filialleiter erhält automatisch eine Information zur Überprüfung dieser Stornierung.
                        </p>
                        <div>
                            <label className="text-[10px] font-bold uppercase tracking-wider text-gray-600">Storno-Grund *</label>
                            <Textarea
                                value={cancelReason}
                                onChange={(e) => setCancelReason(e.target.value)}
                                placeholder="z.B. Kunde hat abgesagt, doppelt erfasst, Teil nicht lieferbar …"
                                className="rounded-sm mt-1 min-h-[70px]"
                                data-testid={`wo-cancel-reason-${order.id}`}
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <Button variant="outline" onClick={() => setCancelAsk(false)} className="rounded-sm text-[12px]" data-testid={`wo-cancel-abort-${order.id}`}>Abbrechen</Button>
                            <Button
                                disabled={!cancelReason.trim()}
                                onClick={() => { setCancelAsk(false); onTransition("cancelled", cancelReason.trim()); }}
                                className="rounded-sm text-[12px] font-bold bg-red-600 hover:bg-red-700 text-white"
                                data-testid={`wo-cancel-confirm-${order.id}`}
                            >
                                <X className="w-3.5 h-3.5 mr-1" /> Ja, stornieren
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Re-export shared constants so consumers can `import { STATUS_META } from
// "../components/workshop/OrderCard"` if they need them.
export { STATUS_META, STATUS_NEXT_LABEL, fmtEur };
